import React, { useCallback, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import ConversationList from "./ConversationList";
import SearchInput from "./SearchInput";

import Loader from "../Loader";
import NewConversationButton from "./NewConversationButton";
import NoConversations from "./NoConversations";
import SettingsButton from "./SettingsButton";

import { useAppSelector, useAppDispatch } from "../../hooks/reduxhook";
import {
  getConversations,
  getFilteredConversations,
  setSearchQuery,
  clearActiveChat,
} from "../../redux/slices/conversationSlice";
import { getloadingforconversations } from "../../redux/slices/loadingSlice";

const drawerWidth = 290;

export default function LeftSidebar() {
  const dispatch = useAppDispatch();
  const conversations = useAppSelector(getConversations);
  const filteredConversations = useAppSelector(getFilteredConversations);
  const loading = useAppSelector(getloadingforconversations);

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({
    Today: true,
    Yesterday: true,
    "Last week": true,
    Older: true,
  });

  // Initialize search from session storage
  useEffect(() => {
    const savedSearch = sessionStorage.getItem("conversation_search") || "";
    if (savedSearch) {
      dispatch(setSearchQuery(savedSearch));
    }
  }, [dispatch]);

  const handleSearch = useCallback(
    (query: string) => {
      dispatch(setSearchQuery(query));
    },
    [dispatch]
  );

  const toggleExpanded = (groupName: string) => {
    setExpanded((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  // const handleNewChat = useCallback(() => {
  //   dispatch(clearActiveChat());
  // }, [dispatch]);

  const displayConversations =
    filteredConversations.length > 0 || conversations.length === 0
      ? filteredConversations
      : conversations;

  const groupConversationsByDate = (convs: any[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    const groups: { [key: string]: any[] } = {
      Today: [],
      Yesterday: [],
      "Last week": [],
      Older: [],
    };

    convs.forEach((conv) => {
      const convDate = new Date(conv?.time);
      const convDay = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());

      if (convDay.getTime() === today.getTime()) {
        groups.Today.push(conv);
      } else if (convDay.getTime() === yesterday.getTime()) {
        groups.Yesterday.push(conv);
      } else if (convDay >= lastWeek) {
        groups["Last week"].push(conv);
      } else {
        groups.Older.push(conv);
      }
    });

    return groups;
  };

  const groupedConversations = groupConversationsByDate(displayConversations);
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: "border-box" },
      }}
    >
      <Toolbar />
      {/* <NewConversationButton onClick={handleNewChat} /> */}
      <NewConversationButton />
      {/* {conversations?.length > 0 && <DeleteAllConversationsButton />} */}
      {conversations?.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
          }}
        >
          <SearchInput onSearch={handleSearch} />
        </Box>
      )}
      {loading && <Loader />}
      <Box
        id="conversationlist"
        sx={{
          overflow: "auto",
          flex: 1,
        }}
      >
        <div>
          {conversations?.length === 0 ? (
            <NoConversations />
          ) : displayConversations?.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
              No conversations found
            </Box>
          ) : (
            Object.entries(groupedConversations).map(([groupName, convs]) =>
              convs.length > 0 ? (
                <Box key={groupName}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => toggleExpanded(groupName)}
                      sx={{ p: 0.5 }}
                    >
                      {expanded[groupName] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.secondary",
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                        flex: 1,
                      }}
                    >
                      {groupName}
                    </Typography>
                  </Box>
                  {expanded[groupName] && convs.map((conv) => (
                    <ConversationList
                      key={conv?.id}
                      name={conv?.conv_name}
                      conversation_id={conv?.id}
                      date={conv?.time}
                    />
                  ))}
                </Box>
              ) : null
            )
          )}
        </div>
      </Box>
      <SettingsButton />
    </Drawer>
  );
}
